import { AlertTriangle, ChevronRight, ListChecks } from "lucide-react";
import {
  isTaskOverdue,
  taskViewSections,
  type Task,
  type TaskView,
} from "../types/tasks";

type Props = {
  active: TaskView;
  tasks: Task[];
  onChange: (value: TaskView) => void;
};

export function TaskSectionNav({ active, tasks, onChange }: Props) {
  const activeCount = tasks.filter((task) => task.estado !== "completado").length;
  const overdueCount = tasks.filter(isTaskOverdue).length;

  return (
    <section className="dashboard-component-shell rounded-[28px] border border-[#dfe8e4] bg-white/90 p-3 shadow-[0_18px_50px_rgba(15,23,42,.065)]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-2.5">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#0b4a2f]">
            Vistas de tareas
          </div>
          <div className="mt-0.5 text-sm font-bold text-slate-500">
            Cambia la presentación sin alterar filtros.
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <CompactMetric icon={ListChecks} label="Activas" value={activeCount} />
          <CompactMetric
            icon={AlertTriangle}
            label="Vencidas"
            value={overdueCount}
            tone="amber"
          />
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        {taskViewSections.map((section) => {
          const Icon = section.icon;
          const isActive = active === section.id;

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => onChange(section.id)}
              className={[
                "group flex min-h-[66px] items-center gap-3 rounded-[20px] border px-3 py-2.5 text-left transition-all duration-200",
                isActive
                  ? "border-[#0b4a2f] bg-[linear-gradient(135deg,#0b4a2f,#0f5b3b)] text-white shadow-[0_12px_28px_rgba(11,74,47,.17)]"
                  : "border-[#dfe8e4] bg-white text-slate-600 hover:border-[#00d09c]/25 hover:bg-[#00d09c]/7 hover:text-[#0b4a2f]",
              ].join(" ")}
            >
              <span
                className={[
                  "grid h-10 w-10 shrink-0 place-items-center rounded-xl border",
                  isActive
                    ? "border-white/12 bg-white/10 text-white"
                    : "border-[#dfe8e4] bg-white text-slate-400 group-hover:text-[#0b4a2f]",
                ].join(" ")}
              >
                <Icon size={17} />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-sm font-black">{section.title}</span>
                <span
                  className={[
                    "mt-0.5 block truncate text-[11px] font-semibold",
                    isActive ? "text-white/70" : "text-slate-400",
                  ].join(" ")}
                >
                  {section.subtitle}
                </span>
              </span>

              <ChevronRight
                size={16}
                className={isActive ? "text-white/80" : "text-slate-300"}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}

function CompactMetric({
  icon: Icon,
  label,
  value,
  tone = "green",
}: {
  icon: typeof ListChecks;
  label: string;
  value: number;
  tone?: "green" | "amber";
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-black text-slate-600">
      <Icon size={13} className={tone === "amber" ? "text-amber-500" : "text-[#0b4a2f]"} />
      {label}: {value}
    </div>
  );
}
